import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API, { ACTIVE_SCHOOL_KEY } from '../BaseURL';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [schoolName, setSchoolName] = useState('');

  // Multi-school (multi-tenant) state. `currentSchool` carries the
  // active school's branding (name, logo, motto, header) so layouts
  // and printed reports can theme themselves per school without
  // hitting the API again. `multiSchoolEnabled` mirrors the vendor
  // flag — routes/UI gate off it. Both are refreshed together via
  // /api/schools/current whenever the user changes.
  const [currentSchool, setCurrentSchool]       = useState(null);
  const [multiSchoolEnabled, setMultiSchoolEnabled] = useState(false);
  const [multiSchoolLoading, setMultiSchoolLoading] = useState(true);

  // Super-users (users.school_id IS NULL) may work in any school.
  // `switchableSchools` is the list they can pick from and
  // `activeSchoolId` is the one currently selected — sent on every
  // request as X-School-Id by the axios interceptor.
  const [switchableSchools, setSwitchableSchools] = useState([]);
  const [canSwitchSchools, setCanSwitchSchools]   = useState(false);
  // Master owner only — the single account allowed to create/manage
  // schools. Separate from canSwitchSchools on purpose: an admin may
  // run several schools without being able to create one.
  const [canManageSchools, setCanManageSchools]   = useState(false);
  // The owner can REQUEST a school even before the module is switched
  // on — the approved request is what switches it on. Keeps the
  // Schools menu reachable on a not-yet-activated deployment.
  const [canRequestSchool, setCanRequestSchool]   = useState(false);
  // Creating a school outright skips the vendor's approval, so it stays
  // with the master owner. Everyone else in the group submits a request.
  const [canCreateSchoolDirectly, setCanCreateSchoolDirectly] = useState(false);
  const [activeSchoolId, setActiveSchoolId] = useState(
    () => localStorage.getItem(ACTIVE_SCHOOL_KEY) || ''
  );

  const loadCurrentSchool = useCallback(async () => {
    setMultiSchoolLoading(true);
    try {
      const res = await API.get('/schools/current');
      setCurrentSchool(res.data?.school || null);
      setMultiSchoolEnabled(!!res.data?.multi_school_enabled);
      setCanSwitchSchools(!!res.data?.can_switch_schools);
      setCanManageSchools(!!res.data?.can_manage_schools);
      setCanRequestSchool(!!res.data?.can_request_school);
      setCanCreateSchoolDirectly(!!res.data?.can_create_school_directly);
      setSwitchableSchools(res.data?.switchable || []);

      // Keep the remembered school honest: if the backend served a
      // different one (the stored id was stale, deactivated, or this
      // user isn't allowed to switch) adopt what it actually gave us.
      const served = res.data?.school?.id;
      if (served && res.data?.can_switch_schools) {
        const stored = localStorage.getItem(ACTIVE_SCHOOL_KEY);
        if (String(served) !== String(stored)) {
          localStorage.setItem(ACTIVE_SCHOOL_KEY, String(served));
          setActiveSchoolId(String(served));
        }
      } else if (!res.data?.can_switch_schools) {
        // Bound user — the header is meaningless and would only
        // confuse a later session on a shared browser.
        localStorage.removeItem(ACTIVE_SCHOOL_KEY);
        setActiveSchoolId('');
      }
    } catch {
      setCurrentSchool(null);
      setMultiSchoolEnabled(false);
      setCanSwitchSchools(false);
      setCanManageSchools(false);
      setCanRequestSchool(false);
      setCanCreateSchoolDirectly(false);
      setSwitchableSchools([]);
    } finally {
      setMultiSchoolLoading(false);
    }
  }, []);

  // Switch the active school.
  //
  // Deliberately a full page reload rather than a state update: every
  // open page holds its own fetched rows (class lists, ledgers,
  // balances) in component state, and there is no reliable way to
  // invalidate all of it from here. Reloading guarantees nothing from
  // the previous school survives on screen — which matters a great
  // deal more than the reload being slightly abrupt.
  const switchSchool = useCallback((schoolId) => {
    if (!schoolId) return;
    localStorage.setItem(ACTIVE_SCHOOL_KEY, String(schoolId));
    setActiveSchoolId(String(schoolId));
    window.location.reload();
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('school_user');
    if (stored) {
      try { setUser(JSON.parse(stored)); } catch { /* ignore */ }
    }
    setLoading(false);

    API.get('/setup/info')
      .then((res) => setSchoolName(res.data?.info?.school_name || ''))
      .catch(() => {});
  }, []);

  // Load per-school branding + flag whenever the signed-in user
  // changes. On logout (user = null) we clear both so a subsequent
  // login on a different school starts clean.
  useEffect(() => {
    if (user) loadCurrentSchool();
    else {
      setCurrentSchool(null);
      setMultiSchoolEnabled(false);
      setCanSwitchSchools(false);
      setCanManageSchools(false);
      setCanRequestSchool(false);
      setCanCreateSchoolDirectly(false);
      setSwitchableSchools([]);
      setMultiSchoolLoading(false);
    }
  }, [user, loadCurrentSchool]);

  const login = (token, userData) => {
    localStorage.setItem('school_token', token);
    localStorage.setItem('school_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('school_token');
    localStorage.removeItem('school_user');
    // Drop the active-school pin too, so the next person to log in on
    // this browser starts in their own school rather than inheriting
    // whichever one the previous user was viewing.
    localStorage.removeItem(ACTIVE_SCHOOL_KEY);
    setActiveSchoolId('');
    setUser(null);
  };

  // Re-fetch latest user (role/pages may have changed)
  const refreshUser = useCallback(async () => {
    try {
      const res = await API.get('/auth/me');
      const u = res.data?.user;
      if (u) {
        localStorage.setItem('school_user', JSON.stringify(u));
        setUser(u);
      }
    } catch { /* ignore */ }
  }, []);

  const refreshSchoolName = useCallback(async () => {
    try {
      const res = await API.get('/setup/info');
      setSchoolName(res.data?.info?.school_name || '');
    } catch { /* ignore */ }
  }, []);

  const hasPage = (key) => user?.role === 'super_admin' || (user?.pages?.includes(key) ?? false);

  return (
    <AuthContext.Provider value={{
      user, loading, schoolName,
      currentSchool, multiSchoolEnabled, multiSchoolLoading,
      switchableSchools, canSwitchSchools, canManageSchools, canRequestSchool,
      canCreateSchoolDirectly, activeSchoolId, switchSchool,
      login, logout, refreshUser, refreshSchoolName, hasPage,
      refreshCurrentSchool: loadCurrentSchool,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
